import "./GlobalInsightsOverlay.scss";

import { useEffect, useState } from "react";
import { FaChevronLeft, FaChevronRight, FaMagic } from "react-icons/fa";
import { useLocation } from "react-router-dom";

import {
  MAPPING_READINESS_POLL_INTERVAL_MS,
  useMappingReadiness,
} from "../../../../api/mappingReadiness";
import { useHasPlatformRole } from "../../../../api/userAccess";
import { useRaidActiveTab } from "../../../../contexts/RaidActiveTabContext";
import { AppRoutes } from "../../../../types/enums/appRoutes";
import { PLANNING_VIEW_ALLOWED_ROLES } from "../../../../types/enums/roleKeys";
import { AiInsightsDay1ReadinessShelf } from "../../../aiInsights/AiInsightsDay1Readiness/AiInsightsDay1ReadinessShelf";
import { AiInsightsDecisionsShelf } from "../../../aiInsights/AiInsightsDecisions/AiInsightsDecisionsShelf";
import { AiInsightsPlanningShelf } from "../../../aiInsights/AiInsightsPlanning/AiInsightsPlanningShelf";
import { AiInsightsRaidAssumptionsShelf } from "../../../aiInsights/AiInsightsRaid/AiInsightsRaidAssumptionsShelf";
import { AiInsightsRaidDependenciesShelf } from "../../../aiInsights/AiInsightsRaid/AiInsightsRaidDependenciesShelf";
import { AiInsightsRaidRisksIssuesShelf } from "../../../aiInsights/AiInsightsRaid/AiInsightsRaidRisksIssuesShelf";
import { AiInsightWaitingState } from "../../../aiInsights/AiInsightWaitingState";
import { AiDisclaimer, Spinner } from "../../../shared";

type InsightPanelKind = "checklist" | "decisions-log" | "planning" | "raid";

function getInsightPanelKind(pathname: string): InsightPanelKind | null {
  if (pathname.startsWith(AppRoutes.checklist)) {
    return "checklist";
  }

  if (pathname.startsWith(AppRoutes.decisionsLog)) {
    return "decisions-log";
  }

  if (pathname.startsWith(AppRoutes.raid)) {
    return "raid";
  }

  if (pathname.startsWith(AppRoutes.integrationPlanning)) {
    return "planning";
  }

  return null;
}

interface GlobalInsightsOverlayProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
}

export function GlobalInsightsOverlay({ isOpen, onOpenChange }: GlobalInsightsOverlayProps) {
  const { pathname } = useLocation();
  const insightPanelKind = getInsightPanelKind(pathname);
  const { activeRaidTab } = useRaidActiveTab();
  const canViewPlanning = useHasPlatformRole(PLANNING_VIEW_ALLOWED_ROLES);
  const isOverlayHiddenForRoute =
    !canViewPlanning ||
    pathname.startsWith(AppRoutes.settings) ||
    pathname === AppRoutes.home ||
    insightPanelKind === null;

  // The panel's contents describe the current page. Close it when navigating
  // between pages, while leaving it open when a RAID sub-tab changes.
  const [previousPathname, setPreviousPathname] = useState(pathname);
  if (pathname !== previousPathname) {
    setPreviousPathname(pathname);
    onOpenChange(false);
  }

  // The overlay stays mounted on hidden routes (it just renders null below),
  // so isOpen can silently remain true. Adjust it during render (React's
  // recommended "state adjustment" pattern) rather than in a useEffect, so
  // navigating back to an enabled route doesn't reopen the overlay
  // unexpectedly.
  const [prevIsOverlayHiddenForRoute, setPrevIsOverlayHiddenForRoute] =
    useState(isOverlayHiddenForRoute);
  if (isOverlayHiddenForRoute !== prevIsOverlayHiddenForRoute) {
    setPrevIsOverlayHiddenForRoute(isOverlayHiddenForRoute);
    if (isOverlayHiddenForRoute) {
      onOpenChange(false);
    }
  }

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onOpenChange(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onOpenChange]);

  const readiness = useMappingReadiness(
    isOpen && !isOverlayHiddenForRoute,
    MAPPING_READINESS_POLL_INTERVAL_MS
  );
  const isWaiting = readiness.data?.mappingReason != null;

  if (isOverlayHiddenForRoute) {
    return null;
  }

  return (
    <div
      className={[
        "global-insights-overlay",
        `global-insights-overlay--${insightPanelKind}`,
        isOpen ? "global-insights-overlay--open" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <button
        type="button"
        className="global-insights-overlay__rail"
        onClick={() => onOpenChange(!isOpen)}
        aria-controls="global-insights-overlay-panel"
        aria-expanded={isOpen}
      >
        <span className="global-insights-overlay__rail-chevron" aria-hidden="true">
          {isOpen ? <FaChevronRight /> : <FaChevronLeft />}
        </span>
        <span className="global-insights-overlay__rail-label">Insights</span>
        <span className="global-insights-overlay__rail-icon" aria-hidden="true">
          <FaMagic />
        </span>
      </button>

      <aside
        id="global-insights-overlay-panel"
        className="global-insights-overlay__panel"
        aria-label="Global insights"
        aria-hidden={!isOpen}
      >
        <div className="global-insights-overlay__header">
          <div className="global-insights-overlay__heading">
            <span className="global-insights-overlay__header-icon" aria-hidden="true">
              <FaMagic />
            </span>
            <h2 className="global-insights-overlay__title">AI insights</h2>
          </div>
        </div>

        {readiness.isLoading ? (
          <section
            className="global-insights-overlay__section global-insights-overlay__section--loading"
            aria-live="polite"
          >
            <Spinner size="lg" />
          </section>
        ) : (
          <div className="global-insights-overlay__content-wrapper">
            <div hidden={isWaiting}>
              {insightPanelKind === "decisions-log" ? (
                <AiInsightsDecisionsShelf isOpen={isOpen} />
              ) : insightPanelKind === "planning" ? (
                <AiInsightsPlanningShelf isOpen={isOpen} />
              ) : insightPanelKind === "checklist" ? (
                <AiInsightsDay1ReadinessShelf isOpen={isOpen} />
              ) : activeRaidTab === "assumptions" ? (
                <AiInsightsRaidAssumptionsShelf isOpen={isOpen} />
              ) : activeRaidTab === "dependencies" ? (
                <AiInsightsRaidDependenciesShelf isOpen={isOpen} />
              ) : (
                <AiInsightsRaidRisksIssuesShelf isOpen={isOpen} />
              )}
            </div>

            {isWaiting ? (
              <div className="global-insights-overlay__waiting-overlay">
                <AiInsightWaitingState reason={readiness.data?.mappingReason} />
              </div>
            ) : null}
          </div>
        )}
        <AiDisclaimer className="global-insights-overlay__disclaimer" />
      </aside>
    </div>
  );
}

export default GlobalInsightsOverlay;
